// Shared SLA computation helpers — used by case panel, occurrence-book list, and map view pins.
// Tone-driven so every surface stays in lock-step.

export const SLA_TONE_META = {
  none:   { label: 'No SLA',     bg: 'bg-slate-100',    text: 'text-slate-500',     dot: 'bg-slate-400'  },
  ok:     { label: 'On track',   bg: 'bg-emerald-50',   text: 'text-emerald-700',   dot: 'bg-emerald-500'},
  amber:  { label: '50%',        bg: 'bg-amber-50',     text: 'text-amber-700',     dot: 'bg-amber-500'  },
  red:    { label: '75% — escalate', bg: 'bg-red-50',   text: 'text-red-700',       dot: 'bg-red-500'    },
  breach: { label: 'BREACHED',   bg: 'bg-red-100',      text: 'text-red-800',       dot: 'bg-red-600'    },
  closed_met: { label: 'Met',    bg: 'bg-emerald-50',   text: 'text-emerald-700',   dot: 'bg-emerald-500'},
  closed_breach: { label: 'Breached', bg: 'bg-red-50',  text: 'text-red-700',       dot: 'bg-red-500'    },
  cancelled: { label: 'Cancelled', bg: 'bg-slate-100',  text: 'text-slate-500',     dot: 'bg-slate-400'  },
};

/**
 * Compute SLA snapshot for an incident.
 * @param {object} incident  - must have opened_at, primary_service_id, status, closed_at?, cancelled_at?
 * @param {object} serviceMap - {[id]: { response_sla_minutes }}
 * @returns {null | {tone, pct, slaMin, elapsedMin, remainingMin}}
 */
export const computeSla = (incident, serviceMap) => {
  if (!incident) return null;
  const svc = serviceMap?.[incident.primary_service_id];
  const slaMin = svc?.response_sla_minutes || 0;
  if (!slaMin) return { tone: 'none', pct: 0, slaMin: 0, elapsedMin: 0, remainingMin: 0 };
  const openedAt = incident.opened_at ? new Date(incident.opened_at).getTime() : null;
  if (!openedAt) return null;
  const isClosed = incident.status === 'closed';
  const isCancelled = incident.status === 'cancelled';
  if (isCancelled) {
    return { tone: 'cancelled', pct: 0, slaMin, elapsedMin: 0, remainingMin: 0 };
  }
  const stopAt = isClosed
    ? new Date(incident.closed_at || openedAt).getTime()
    : Date.now();
  const elapsedMin = (stopAt - openedAt) / 60000;
  const remainingMin = slaMin - elapsedMin;
  const pct = Math.min(999, (elapsedMin / slaMin) * 100);
  let tone;
  if (isClosed) tone = pct < 100 ? 'closed_met' : 'closed_breach';
  else if (pct >= 100) tone = 'breach';
  else if (pct >= 75) tone = 'red';
  else if (pct >= 50) tone = 'amber';
  else tone = 'ok';
  return { tone, pct, slaMin, elapsedMin, remainingMin };
};

/** Short, list-friendly string e.g. "12 min left", "+5 min over", "Met (8m)" */
export const fmtSlaShort = (sla) => {
  if (!sla) return '—';
  if (sla.tone === 'none') return 'No SLA';
  if (sla.tone === 'cancelled') return 'N/A';
  if (sla.tone === 'closed_met') return `Met (${Math.floor(sla.elapsedMin)}m)`;
  if (sla.tone === 'closed_breach') return `+${Math.floor(sla.elapsedMin - sla.slaMin)}m late`;
  if (sla.tone === 'breach') {
    const over = Math.max(0, Math.floor(-sla.remainingMin));
    return over >= 1 ? `+${over}m over` : 'over';
  }
  if (sla.remainingMin >= 1) return `${Math.floor(sla.remainingMin)}m left`;
  if (sla.remainingMin >= 0) return '< 1m left';
  return 'over';
};
